import {
  BulkMessage,
  BulkMessageFilter,
  BulkMessageQuery,
  BulkMessageSenderTypeEnum,
  BulkMessageStartTermInput,
  FunnelStateEnum,
  MessagePart,
  MessagePartInput,
  Student,
  StudentConnection,
  UnitEnum,
} from "@/__generated__/graphql";
import {
  FormFunnelState,
  FormValues,
  FormValuesFilters,
  MessagePartInputExtended,
} from "@/pages/BulkMessaging/types";
import { timestampToDate } from "@/util/dateUtils";
import { FunnelStateMaps } from "@/util/funnelGroups";
import { secondsToMilliseconds } from "date-fns";

export function basicFormatting(
  data?: BulkMessageQuery | null,
): BulkMessage | undefined {
  if (data?.node?.__typename === "BulkMessage") {
    const { createdAt, startSendingAt, stopSendingAt, ...rest } =
      data.node ?? {};

    return {
      createdAt: secondsToMilliseconds(createdAt),
      startSendingAt: secondsToMilliseconds(startSendingAt),
      stopSendingAt: secondsToMilliseconds(stopSendingAt),
      ...rest,
    } as BulkMessage;
  }
}

interface TransformOptionalParams {
  timeZone?: string | null;
  edit?: boolean;
}

export function transformIntoFormData(
  node?: Partial<BulkMessage>,
  options?: TransformOptionalParams,
): FormValues | undefined {
  const { timeZone, edit } = options || {};
  if (node?.__typename !== "BulkMessage") {
    return;
  } else {
    const { filters } = node;

    return {
      name: node.name,
      filters: buildFormFilters(filters),
      messageParts: buildMessageParts(node.messageParts),
      sender: buildSender(node.senderType, node?.sentFrom?.id),
      sendOption: buildSendOption(node.startSendingAt, node.stopSendingAt, {
        timeZone,
        edit,
      }),
    };
  }
}

export function buildFormFilters(
  filters?: BulkMessageFilter,
): FormValuesFilters {
  if (!filters) {
    return {};
  }
  const {
    assignedCounselorIds,
    commitIndexes,
    firstTimeMessageRecipients,
    interestIds,
    funnelStates,
    locations,
    majorIds,
    startTerms,
    studentSource,
    studentTypes,
    userIds,
  } = filters;
  return {
    firstTimeMessageRecipients: firstTimeMessageRecipients?.at(0),
    assignedCounselors: assignedCounselorIds || [],
    commitIndex: commitIndexes || [],
    interests: interestIds || [],
    funnelStates: buildFunnelStates(funnelStates || []),
    location: locations || [],
    majors: majorIds || [],
    startTerm: startTerms?.map(
      ({ semester, year }) =>
        ({
          semester,
          year,
        }) as BulkMessageStartTermInput,
    ),
    studentSource: studentSource || [],
    studentType: studentTypes || [],
    userIds: userIds || [],
  };
}

export function buildFunnelStates(
  funnelStates: FunnelStateEnum[],
): FormFunnelState[] {
  const insightsMapping = Object.entries(FunnelStateMaps["SIDEBAR"]);
  const mappedStates = funnelStates
    .map((funnelState) => {
      const insightFunnel = insightsMapping.find(([, enums]) =>
        enums.includes(funnelState),
      );
      if (insightFunnel) {
        return insightFunnel[0] as FormFunnelState;
      }
    })
    .flat()
    .filter((funnelState) => funnelState !== undefined);

  return [...new Set(mappedStates)];
}

export function buildSender(
  senderType?: BulkMessageSenderTypeEnum,
  senderId?: string,
) {
  if (senderType === BulkMessageSenderTypeEnum.AssignedCounselor) {
    return { assignedCounselor: UnitEnum.Unit };
  } else if (senderId) {
    return { userId: senderId };
  }
}

export function buildSendOption(
  startSendingAt: number,
  stopSendingAt: number,
  options?: TransformOptionalParams,
) {
  const { timeZone, edit } = options || {};

  if (!startSendingAt) {
    // We can't actually tell if a draft was saved as send now. But, you can't
    // edit sent messages, so this is a safe assumption
    return edit ? undefined : { sendNow: UnitEnum.Unit };
  } else if (startSendingAt === stopSendingAt || !stopSendingAt) {
    return {
      sendLater: {
        startSendingAt: timestampToDate(startSendingAt, timeZone),
      },
    };
  } else {
    return {
      startCampaign: {
        startSendingAt: timestampToDate(startSendingAt, timeZone),
        stopSendingAt: timestampToDate(stopSendingAt, timeZone),
      },
    };
  }
}

export function studentsFromApi(studentConnection?: StudentConnection) {
  return studentConnection?.__typename === "StudentConnection"
    ? studentConnection?.edges
        ?.filter((edge) => edge && edge?.node?.__typename === "Student")
        ?.map((edge) => edge?.node as Student) || []
    : [];
}

export function buildMessageParts(
  messagePartsConnection?: {
    edges?: ({ node?: MessagePart | null } | null)[] | null;
  } | null,
): MessagePartInputExtended[] {
  if (!messagePartsConnection?.edges?.length) {
    return [];
  }

  return messagePartsConnection.edges
    .map((edge) => edge?.node)
    .filter((node): node is MessagePart => node !== null && node !== undefined)
    .map((messagePart) => {
      if (messagePart.body.__typename === "MessagePartText") {
        return {
          update: {
            messagePartId: messagePart.id,
            body: { text: messagePart.body.text || "" },
          },
        } as MessagePartInput;
      } else if (messagePart.body.__typename === "MessagePartAttachment") {
        return {
          update: {
            messagePartId: messagePart.id,
            body: { url: messagePart.body.url ?? "" },
          },
        } as MessagePartInputExtended;
      }
      return null;
    })
    .filter((part): part is MessagePartInputExtended => part !== null);
}

export function createTextMessagePart(text = ""): MessagePartInput {
  return {
    create: {
      body: { text },
    },
  };
}

export function createImageMessagePart(file: File): MessagePartInput {
  return {
    create: {
      body: { attachment: file },
    },
  };
}

export function createEmptyMessagePart(): MessagePartInput {
  return createTextMessagePart("");
}

export function getMessagePartContent(
  messagePart: MessagePartInput | MessagePartInputExtended,
): {
  text?: string;
  attachment?: File | { type: string; url: string };
  isImageElement?: boolean;
} {
  if (messagePart.create?.body.text !== undefined) {
    return {
      text: messagePart.create.body.text,
      isImageElement: messagePart.create.body.text === "__IMAGE_ELEMENT__",
    };
  }

  if (messagePart.create?.body.attachment) {
    return {
      attachment: messagePart.create.body.attachment,
    };
  }

  if (
    messagePart.update?.body &&
    "text" in messagePart.update.body &&
    messagePart.update.body.text !== undefined
  ) {
    return {
      text: messagePart.update.body.text,
    };
  }

  if (
    messagePart.update?.body &&
    "url" in messagePart.update.body &&
    messagePart.update.body.url
  ) {
    return {
      attachment: { type: "IMAGE", url: messagePart.update.body.url },
    };
  }

  return {};
}
